import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { fetchOrphans, fetchTrips } from '../features/my-trips/api';
import { OrphanSummary } from '../features/my-trips/types';
import TripDetailNav from '../features/my-trips/components/TripDetailNav';
import AssignRow from '../features/my-trips/components/AssignRow';
import ApproxNote from '../features/my-trips/components/ApproxNote';
import { TripOption } from '../features/my-trips/components/AssignDropdown';
import { useDocumentTitle } from '../shared/useDocumentTitle';

// Unsorted journeys page (design-handoff.md C.4). A clean list of orphan
// journeys; each row files into a trip (or a new trip) without leaving the
// page, and tapping the row opens the shared Journey detail page.
const UnsortedJourneysPage: React.FC = () => {
  useDocumentTitle('Unsorted journeys');
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [orphans, setOrphans] = useState<OrphanSummary[]>([]);
  const [trips, setTrips] = useState<TripOption[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [orphanList, tripsRes] = await Promise.all([fetchOrphans(), fetchTrips()]);
      setOrphans(orphanList);
      setTrips(tripsRes.trips.map((t) => ({ id: t.id, title: t.title })));
    } catch (e) {
      showToast({ title: 'Error', message: 'Failed to load unsorted journeys.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '20px 16px 48px' }}>
        <TripDetailNav title="Unsorted journeys" onBack={() => navigate(-1)} />
        <p style={{ color: '#6c7872', fontSize: 14, margin: '0 0 16px' }}>
          Journeys not yet filed under a trip. Assign each to a trip, or open it to view the route.
        </p>

        {loading ? (
          <div style={{ color: '#6c7872', padding: '24px 0' }}>Loading…</div>
        ) : orphans.length === 0 ? (
          <div style={{ background: '#ffffff', borderRadius: 16, padding: '28px 20px', textAlign: 'center', color: '#6c7872' }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>✅</div>
            <div style={{ fontWeight: 600, color: '#16241d', marginBottom: 4 }}>Nothing unsorted</div>
            <div style={{ fontSize: 14 }}>Every journey is filed under a trip.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {orphans.map((orphan) => (
              <AssignRow key={orphan.id} orphan={orphan} trips={trips} onAssigned={load} />
            ))}
            <ApproxNote align="left" />
          </div>
        )}
      </div>
    </div>
  );
};

export default UnsortedJourneysPage;
